import axios, { type AxiosInstance } from 'axios';
import type {
  Account,
  ApiResponse,
  AuthResponse,
  CallLog,
  Contact,
  CreateAccountInput,
  CreateCallLogInput,
  CreateContactInput,
  CreateDealInput,
  CreateDepartmentInput,
  CreateEmployeeInput,
  CreateLeadInput,
  CreateMeetingInput,
  CreateTaskInput,
  DashboardStats,
  Deal,
  Department,
  Employee,
  Lead,
  ListParams,
  LoginInput,
  Meeting,
  ModulesCatalogResponse,
  PaginatedResponse,
  PlatformModule,
  Permission,
  Pipeline,
  PipelineBoard,
  Plan,
  RegisterCompanyInput,
  ReportsSummary,
  Role,
  SuperAdmin,
  Task,
  Tenant,
  TenantModulesResponse,
  TenantStats,
  UpdateLeadInput,
  User,
} from '@zcmss/types';

export interface ApiClientConfig {
  baseUrl: string;
  getToken?: () => string | null;
  getTenant?: () => string | null;
  onUnauthorized?: () => void;
}

export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const client = axios.create({
    baseURL: config.baseUrl,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  client.interceptors.request.use((req) => {
    const token = config.getToken?.();
    if (token) req.headers.Authorization = `Bearer ${token}`;

    const tenant = config.getTenant?.();
    if (tenant) req.headers['X-Tenant'] = tenant;

    return req;
  });

  client.interceptors.response.use(
    (res) => res,
    (error) => {
      if (error.response?.status === 401) config.onUnauthorized?.();
      return Promise.reject(error);
    }
  );

  return client;
}

export function createCentralApi(client: AxiosInstance) {
  return {
    getPlans: () => client.get<ApiResponse<Plan[]>>('/central/plans').then((r) => r.data.data),

    checkSubdomain: (subdomain: string) =>
      client
        .get<ApiResponse<{ subdomain: string; available: boolean }>>('/central/check-subdomain', {
          params: { subdomain },
        })
        .then((r) => r.data.data),

    register: (data: RegisterCompanyInput) =>
      client
        .post<
          ApiResponse<{
            tenant_id: string;
            subdomain: string;
            portal_url: string;
            status: string;
          }>
        >('/central/register', data)
        .then((r) => r.data.data),

    getTenantStatus: (tenantId: string) =>
      client.get<ApiResponse<Tenant>>(`/central/tenants/${tenantId}/status`).then((r) => r.data.data),

    superAdminLogin: (data: LoginInput) =>
      client
        .post<ApiResponse<AuthResponse>>('/central/auth/login', data)
        .then((r) => r.data.data),

    superAdminLogout: () => client.post('/central/auth/logout'),

    superAdminMe: () =>
      client.get<ApiResponse<SuperAdmin>>('/central/auth/me').then((r) => r.data.data),

    getTenants: (params?: { page?: number; status?: string; search?: string }) =>
      client
        .get<PaginatedResponse<Tenant>>('/central/tenants', { params })
        .then((r) => r.data),

    getTenantStats: () =>
      client.get<ApiResponse<TenantStats>>('/central/tenants/stats').then((r) => r.data.data),

    getModules: () =>
      client.get<ApiResponse<ModulesCatalogResponse>>('/central/modules').then((r) => r.data.data),

    getTenant: (id: string) =>
      client.get<ApiResponse<Tenant>>(`/central/tenants/${id}`).then((r) => r.data.data),

    getTenantModules: (id: string) =>
      client
        .get<ApiResponse<TenantModulesResponse>>(`/central/tenants/${id}/modules`)
        .then((r) => r.data.data),

    updateTenantModules: (id: string, moduleIds: string[]) =>
      client
        .put<
          ApiResponse<{
            tenant_id: string;
            enabled_module_ids: string[];
            enabled_module_slugs: string[];
          }>
        >(`/central/tenants/${id}/modules`, { module_ids: moduleIds })
        .then((r) => r.data.data),

    retryProvision: (
      id: string,
      data?: { admin_name?: string; admin_email?: string; admin_password?: string }
    ) => client.post(`/central/tenants/${id}/retry-provision`, data),

    suspendTenant: (id: string, reason: string) =>
      client.post(`/central/tenants/${id}/suspend`, { reason }),

    restoreTenant: (id: string) => client.post(`/central/tenants/${id}/restore`),

    deleteTenant: (id: string) => client.delete(`/central/tenants/${id}`),
  };
}

export function createTenantApi(client: AxiosInstance) {
  return {
    getContext: () => client.get<ApiResponse<Tenant>>('/tenant/context').then((r) => r.data.data),

    login: (data: LoginInput) =>
      client.post<ApiResponse<AuthResponse>>('/auth/login', data).then((r) => r.data.data),

    logout: () => client.post('/auth/logout'),

    me: () =>
      client
        .get<ApiResponse<{ user: User; permissions: string[] }>>('/auth/me')
        .then((r) => r.data.data),

    getDashboardStats: () =>
      client.get<ApiResponse<DashboardStats>>('/dashboard/stats').then((r) => r.data.data),

    getReportsSummary: () =>
      client.get<ApiResponse<ReportsSummary>>('/reports/summary').then((r) => r.data.data),

    leads: {
      list: (params?: ListParams) =>
        client.get<PaginatedResponse<Lead>>('/leads', { params }).then((r) => r.data),

      get: (id: string) =>
        client.get<ApiResponse<Lead>>(`/leads/${id}`).then((r) => r.data.data),

      create: (data: CreateLeadInput) =>
        client.post<ApiResponse<Lead>>('/leads', data).then((r) => r.data.data),

      update: (id: string, data: UpdateLeadInput) =>
        client.put<ApiResponse<Lead>>(`/leads/${id}`, data).then((r) => r.data.data),

      delete: (id: string) => client.delete(`/leads/${id}`),

      convert: (id: string) =>
        client.post<ApiResponse<Contact>>(`/leads/${id}/convert`).then((r) => r.data.data),
    },

    contacts: {
      list: (params?: ListParams) =>
        client.get<PaginatedResponse<Contact>>('/contacts', { params }).then((r) => r.data),

      get: (id: string) =>
        client.get<ApiResponse<Contact>>(`/contacts/${id}`).then((r) => r.data.data),

      create: (data: CreateContactInput) =>
        client.post<ApiResponse<Contact>>('/contacts', data).then((r) => r.data.data),

      update: (id: string, data: Partial<CreateContactInput>) =>
        client.put<ApiResponse<Contact>>(`/contacts/${id}`, data).then((r) => r.data.data),

      delete: (id: string) => client.delete(`/contacts/${id}`),
    },

    accounts: {
      list: (params?: ListParams) =>
        client.get<PaginatedResponse<Account>>('/accounts', { params }).then((r) => r.data),

      get: (id: string) =>
        client.get<ApiResponse<Account>>(`/accounts/${id}`).then((r) => r.data.data),

      create: (data: CreateAccountInput) =>
        client.post<ApiResponse<Account>>('/accounts', data).then((r) => r.data.data),

      update: (id: string, data: Partial<CreateAccountInput>) =>
        client.put<ApiResponse<Account>>(`/accounts/${id}`, data).then((r) => r.data.data),

      delete: (id: string) => client.delete(`/accounts/${id}`),
    },

    deals: {
      list: (params?: ListParams) =>
        client.get<PaginatedResponse<Deal>>('/deals', { params }).then((r) => r.data),

      get: (id: string) =>
        client.get<ApiResponse<Deal>>(`/deals/${id}`).then((r) => r.data.data),

      create: (data: CreateDealInput) =>
        client.post<ApiResponse<Deal>>('/deals', data).then((r) => r.data.data),

      update: (id: string, data: Partial<CreateDealInput>) =>
        client.put<ApiResponse<Deal>>(`/deals/${id}`, data).then((r) => r.data.data),

      delete: (id: string) => client.delete(`/deals/${id}`),

      moveStage: (id: string, stageId: string) =>
        client
          .post<ApiResponse<Deal>>(`/deals/${id}/move-stage`, { stage_id: stageId })
          .then((r) => r.data.data),

      markWon: (id: string) =>
        client.post<ApiResponse<Deal>>(`/deals/${id}/mark-won`).then((r) => r.data.data),

      markLost: (id: string, reason?: string) =>
        client
          .post<ApiResponse<Deal>>(`/deals/${id}/mark-lost`, { lost_reason: reason })
          .then((r) => r.data.data),
    },

    pipelines: {
      list: () => client.get<ApiResponse<Pipeline[]>>('/pipelines').then((r) => r.data.data),

      getBoard: (id: string) =>
        client.get<ApiResponse<PipelineBoard>>(`/pipelines/${id}/board`).then((r) => r.data.data),
    },

    tasks: {
      list: (params?: ListParams) =>
        client.get<PaginatedResponse<Task>>('/tasks', { params }).then((r) => r.data),

      get: (id: string) =>
        client.get<ApiResponse<Task>>(`/tasks/${id}`).then((r) => r.data.data),

      create: (data: CreateTaskInput) =>
        client.post<ApiResponse<Task>>('/tasks', data).then((r) => r.data.data),

      update: (id: string, data: Partial<CreateTaskInput & { status: Task['status'] }>) =>
        client.put<ApiResponse<Task>>(`/tasks/${id}`, data).then((r) => r.data.data),

      delete: (id: string) => client.delete(`/tasks/${id}`),
    },

    meetings: {
      list: (params?: ListParams) =>
        client.get<PaginatedResponse<Meeting>>('/meetings', { params }).then((r) => r.data),

      get: (id: string) =>
        client.get<ApiResponse<Meeting>>(`/meetings/${id}`).then((r) => r.data.data),

      create: (data: CreateMeetingInput) =>
        client.post<ApiResponse<Meeting>>('/meetings', data).then((r) => r.data.data),

      update: (id: string, data: Partial<CreateMeetingInput>) =>
        client.put<ApiResponse<Meeting>>(`/meetings/${id}`, data).then((r) => r.data.data),

      delete: (id: string) => client.delete(`/meetings/${id}`),
    },

    callLogs: {
      list: (params?: ListParams & { direction?: string; user_id?: string }) =>
        client.get<PaginatedResponse<CallLog>>('/call-logs', { params }).then((r) => r.data),

      get: (id: string) =>
        client.get<ApiResponse<CallLog>>(`/call-logs/${id}`).then((r) => r.data.data),

      create: (data: CreateCallLogInput) =>
        client.post<ApiResponse<CallLog>>('/call-logs', data).then((r) => r.data.data),

      update: (id: string, data: Partial<CreateCallLogInput>) =>
        client.put<ApiResponse<CallLog>>(`/call-logs/${id}`, data).then((r) => r.data.data),

      delete: (id: string) => client.delete(`/call-logs/${id}`),
    },

    departments: {
      list: (params?: ListParams) =>
        client.get<PaginatedResponse<Department>>('/departments', { params }).then((r) => r.data),

      get: (id: string) =>
        client.get<ApiResponse<Department>>(`/departments/${id}`).then((r) => r.data.data),

      create: (data: CreateDepartmentInput) =>
        client.post<ApiResponse<Department>>('/departments', data).then((r) => r.data.data),

      update: (id: string, data: Partial<CreateDepartmentInput>) =>
        client.put<ApiResponse<Department>>(`/departments/${id}`, data).then((r) => r.data.data),

      delete: (id: string) => client.delete(`/departments/${id}`),
    },

    employees: {
      list: (params?: ListParams) =>
        client.get<PaginatedResponse<Employee>>('/employees', { params }).then((r) => r.data),

      get: (id: string) =>
        client.get<ApiResponse<Employee>>(`/employees/${id}`).then((r) => r.data.data),

      create: (data: CreateEmployeeInput) =>
        client.post<ApiResponse<Employee>>('/employees', data).then((r) => r.data.data),

      update: (id: string, data: Partial<Omit<CreateEmployeeInput, 'password'>> & { password?: string }) =>
        client.put<ApiResponse<Employee>>(`/employees/${id}`, data).then((r) => r.data.data),

      delete: (id: string) => client.delete(`/employees/${id}`),
    },

    roles: {
      list: () => client.get<ApiResponse<Role[]>>('/roles').then((r) => r.data.data),

      updatePermissions: (id: string, permissions: string[]) =>
        client
          .put<ApiResponse<Role>>(`/roles/${id}/permissions`, { permissions })
          .then((r) => r.data.data),
    },

    permissions: {
      list: () =>
        client.get<ApiResponse<Permission[]>>('/permissions').then((r) => r.data.data),
    },
  };
}

export type CentralApi = ReturnType<typeof createCentralApi>;
export type TenantApi = ReturnType<typeof createTenantApi>;
